/**
 * ObjectTable React component
 * 
 * @author    Maurizio Tidei, Jens Gruschel
 * @copyright © 2018 contexagon GmbH
 */


// react
import React from 'react';
import PropTypes from 'prop-types';
import {connect} from "react-redux";

// material UI
import {withStyles} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from "@material-ui/core/Tooltip";
import Badge from '@material-ui/core/Badge';

// 3rd party
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

// icons
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/EditOutlined';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import UndoIcon from '@material-ui/icons/Undo';
import FileCopyIcon from '@material-ui/icons/FileCopyOutlined';
import CheckboxIcon from '@material-ui/icons/CheckBox';
import CheckboxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import SwapIcon from '@material-ui/icons/SwapCalls';
import HelpIcon from '@material-ui/icons/Help';
import LockIcon from '@material-ui/icons/Lock';
import ChatIcon from '@material-ui/icons/Chat';
import SmsFailedIcon from '@material-ui/icons/SmsFailed';

// application components
import TagList from "../base/TagList";
import PageSelection from "../base/PageSelection";
import FallbackImage from "../base/FallbackImage";

// application
import {selectFieldValue, selectFieldName, selectTableFieldsMap} from "../../redux/types/selectors";
import {selectTranslation} from "../../redux/configuration/selectors";

// libraries
import _ from "lodash";


const itemsPerPage = 20;


const getTableBodyStyle = isDraggingOver => {
    // no special style yet
}

const getRowStyle = (isDragging) => isDragging ? {
    userSelect: "none",
    display: "table",
    background: "#eee"
} : {
    userSelect: "none"
};


/**
 * ObjectTable shows a list of objects in a table
 *
 * @author Maurizio Tidei, Jens Gruschel
 */
export class ObjectTable extends React.Component {

    state = {
        pageIndex: 0
    };

    onDragEnd(result) {
        if (this.props.onMove && result.source && result.destination && result.source.index !== result.destination.index) {
            this.props.onMove(result.source.index, result.destination.index);
        }
    }

    render() {

        // extract single page
        const pageCount = this.props.pagination ? Math.ceil(this.props.items.length / itemsPerPage) : 1;
        const pageIndex = this.props.pagination ? Math.min(this.state.pageIndex, pageCount - 1) : 0;
        const startIndex = pageIndex * itemsPerPage;
        const pageItems = this.props.pagination ? this.props.items.slice(startIndex, startIndex + itemsPerPage) : this.props.items
    
        // get style
        const {classes} = this.props;

        return <div>
            <Paper className={this.props.asField ? classes.rootAsField : classes.root}>
                <Table onMouseDown={() => {
                        // this is neccessary to distinguish between text selection and clicks,
                        // (otherwise not all mouse down events would clear the selection, e.g. on buttons)
                        if (window.getSelection().empty) {
                            window.getSelection().empty();
                        }
                    }}>
                    <thead className={classes.tableHead}>
                        {this.renderHeadRow()}
                    </thead>
                    {
                        this.props.onMove && !this.props.readOnly ? this.renderDroppableBody(pageItems) : this.renderTableBody(pageItems)
                    }
                </Table>
                {this.renderButtons()}
            </Paper>
            {this.props.pagination ? <PageSelection pageNumber={pageIndex + 1} pageCount={pageCount} onChange={(number) => this.setState({pageIndex: number - 1})}/> : null}
        </div>;
    }

    renderHeadRow() {

        const {classes} = this.props;
        const headClass = this.props.asField ? classes.headCellAsField : classes.headCell;

        return <tr className={classes.headRow}>
            <TableCell className={headClass}>Typ</TableCell>
            {this.props.hasImages && <TableCell className={headClass}>Bild</TableCell>}
            {/* <TableCell className={headClass}>Titel</TableCell> */}
            {this.props.commonFields.map(fieldId =>
                <TableCell key={"head-" + fieldId} className={headClass}>{this.props.fieldNameMap[fieldId]}</TableCell>
            )}
            {this.props.hasUncommonFields && <TableCell className={headClass}>Beschreibung</TableCell>}
            <TableCell className={headClass}>Tags</TableCell>
            <TableCell className={headClass} align="right">Aktionen</TableCell>
        </tr>;
    }

    renderDroppableBody(pageItems) {
        return <DragDropContext onDragEnd={(result) => this.onDragEnd(result)}>
            <Droppable droppableId="objectTableBody">
                {(provided, snapshot) => <tbody className={this.props.classes.tableBody}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={getTableBodyStyle(snapshot.isDraggingOver)}
                >
                    {pageItems.map((item, index) => this.renderDraggableRow(item, index))}
                    {provided.placeholder}
                </tbody>}
            </Droppable>
        </DragDropContext>;
    }

    renderTableBody(pageItems) {

        return <tbody className={this.props.classes.tableBody}>
            {pageItems.map((item, index) => this.renderRow(item, index))}
        </tbody>;
    }

    renderDraggableRow(item, index) {

        return <Draggable key={item.id || index} draggableId={item.id || "draggable-" + index} index={index}>
            {(provided, snapshot) => this.renderRow(item, index, provided, snapshot)}
        </Draggable>;
    }

    renderRow(item, index, provided = null, snapshot = null) {

        const {classes} = this.props;

        const typeInfo = this.getTypeInfo(item);
        const accessInfo = item.accessInfo || {};
        const itemAvailable = !!item.type; // all items need a type - if no type is present, the item is not available at all (maybe because of missing privileges)
        const itemDeleted = !!item['timestamp-deleted'];
        const listReadonly = this.props.readOnly;
        const itemReadonly = listReadonly || !accessInfo.writable;
        const itemDeleteable = accessInfo.deletable;

        const onClick = (event) => {
            if (!window.getSelection().toString()) {
                // only trigger clicks if the user did not select any text
                this.props.onSelect(index, item, event);
            }
        };

        const additionalProps = provided ? {...provided.draggableProps, ...provided.dragHandleProps} : {}
        const additionalStyle = snapshot && provided ? {...getRowStyle(snapshot.isDragging), ...provided.draggableProps.style} : {}
        const typeIcon = this.decorateIcon(
            typeInfo.iconurl ? <img
                className={classes.iconImage}
                src={typeInfo.iconurl}
                alt={typeInfo.name}
                width={24}
                height={24}
            /> : <HelpIcon/>,
            typeInfo.name, item);

        return (
            <tr className={classes.tableRow}
                key={item.id || index}
                ref={provided && provided.innerRef}
                {...additionalProps}
                style={additionalStyle}
            >
                <TableCell onClick={itemAvailable && onClick} className={this.props.classes.tableCell} style={{cursor:"pointer"}}>
                    {typeIcon}
                </TableCell>
                {this.props.hasImages && this.renderImageCell(item, onClick)}
                {/* this.renderFieldCell(item, "#title", onClick) */}
                {this.props.commonFields.map(fieldId =>
                    this.renderFieldCell(item, fieldId, onClick)
                )}
                {this.props.hasUncommonFields && this.renderUncommonFieldsCell(item, onClick)}
                {item.tags ? <TableCell className={classes.rowTagsCell}><TagList tags={item.tags} onTagClick={this.props.onSearchTag}/></TableCell> : <TableCell/>}
                <TableCell className={classes.rowActionCell} align="right">
                    {this.props.onEdit && !itemDeleted && <Tooltip title="Bearbeiten">
                        <IconButton disabled={!itemAvailable} className={classes.rowActionButton} onClick={() => this.props.onEdit(index, item)}><EditIcon/></IconButton>
                    </Tooltip>}
                    {this.props.onRemove && !listReadonly && <Tooltip title="Entfernen">
                        <IconButton className={classes.rowActionButton} onClick={() => this.props.onRemove(index, item)}><RemoveCircleOutlineIcon/></IconButton>
                    </Tooltip>}
                    {this.props.onDelete && !itemDeleted && <Tooltip title="Löschen">
                        <IconButton disabled={!itemDeleteable} className={classes.rowActionButton} onClick={() => this.props.onDelete(index, item)}><DeleteIcon/></IconButton>
                    </Tooltip>}
                    {this.props.onUndelete && itemDeleted && <Tooltip title="Wiederherstellen">
                        <IconButton disabled={itemReadonly} className={classes.rowActionButton} onClick={() => this.props.onUndelete(index, item)}><UndoIcon/></IconButton>
                    </Tooltip>}
                    {this.props.onDuplicate && !itemDeleted && !listReadonly && <Tooltip title="Duplizieren">
                        <IconButton disabled={!itemAvailable} className={classes.rowActionButton} onClick={() => this.props.onDuplicate(index, item)}><FileCopyIcon/></IconButton>
                    </Tooltip>}
                </TableCell>
            </tr>
        );
    }

    renderImageCell(item, onClick) {
        const thumbnailUrl = item["thumbnailurl"];
        const imageUrl = item["imageurl"];
        if (thumbnailUrl || imageUrl) {
            return <TableCell key={"#imageurl"} onClick={onClick} className={this.props.classes.tableCell} style={{cursor:"pointer"}}>
                <FallbackImage urls={[thumbnailUrl, imageUrl]} title={"preview"} className={this.props.classes.thumbnail} />
            </TableCell>;
        }
        else {
            return <TableCell key={"#imageurl"} />;
        }
    }

    renderFieldCell(item, fieldId, onClick) {
        const value = this.extractFieldValue(item, fieldId);
        if (_.isBoolean(value)) {
            return <TableCell key={fieldId} onClick={onClick} className={this.props.classes.tableCell} style={{cursor:"pointer"}}>
                <div className={this.props.classes.cellValue}>{value ? <CheckboxIcon/> : <CheckboxOutlineBlankIcon/>}</div>
            </TableCell>
    }
        else {
            return <TableCell key={fieldId} onClick={onClick} className={this.props.classes.tableCell} style={{cursor:"pointer"}}>
                <div className={this.props.classes.cellValue}>{this.props.translate(value)}</div>
            </TableCell>
        }
    }

    renderUncommonFieldsCell(item, onClick) {
        const uncommonFields = this.props.uncommonFieldsMap[item.type] || [];
        const values = uncommonFields.map(fieldId => this.extractFieldValue(item, fieldId));
        const translatedValues = values.filter(value => !!value).map(value => this.props.translate(value));
        const text = translatedValues.join(", ");
        return <TableCell key={"__uncommon"} onClick={onClick} className={this.props.classes.tableCell} style={{cursor:"pointer"}}>
            <div className={this.props.classes.cellValue}>{text}</div>
        </TableCell>
}

    renderButtons() {
        return <>
            {
                this.props.onReplace && !this.props.readOnly && <Tooltip title="Austauschen">
                <Button className={this.props.classes.addButton} onClick={this.props.onReplace}>
                    <SwapIcon/>
                </Button>
            </Tooltip>
            }
            {
                this.props.onAdd && !this.props.readOnly && <Tooltip title="Hinzufügen">
                    <Button className={this.props.classes.addButton} onClick={this.props.onAdd}>
                        <AddIcon/>
                    </Button>
                </Tooltip>
            }
        </>;
    }

    extractFieldValue(object, fieldId) {
        return selectFieldValue(this.props.types, this.props.objectMap, object, fieldId, true);
    }

    getTypeInfo(item) {
        if (item.type === 'mediaitem') {
            if (item.mediainfo) {
                if (item.mediainfo.type === 'video') {
                    return {
                        iconurl: 'icons/material/baseline/baseline-movie-24px.svg',
                        name: 'Video'
                    };
                }
                else if (item.mediainfo.type === 'image') {
                    return {
                        iconurl: 'icons/material/baseline/baseline-image-24px.svg',
                        name: 'Bild'
                    };
                }
                else if (item.mediainfo.type === 'audio') {
                    return {
                        iconurl: 'icons/material/twotone/twotone-audiotrack-24px.svg',
                        name: 'Audio'
                    };
                }
            }
        }
        return this.props.typeInfoMap[item.type] || {};
    }

    decorateIcon(icon, name, item) {
        const accessRestricted = item.objectaccess &&
            item.objectaccess.accessibility &&
            item.objectaccess.accessibility !== "writable";
        var text = name || "?";
        if (accessRestricted) text += " - geschützt";
        if (item.remarks) text += " - mit Bemerkungen";
        if (item.todo) text += " - unerledigt";
        const result = <Tooltip title={text}>{icon}</Tooltip>;
        if (item.todo) {
            return <Badge
                badgeContent={<SmsFailedIcon className={this.props.classes.badgeIcon} />}
                classes={{badge: this.props.classes.redBadge}}
            >{result}</Badge>;
        }
        else if (accessRestricted) {
            return <Badge
                badgeContent={<LockIcon className={this.props.classes.badgeIcon} />}
                classes={{badge: this.props.classes.grayBadge}}
            >{result}</Badge>;
        }
        else if (item.remarks) {
            return <Badge
                badgeContent={<ChatIcon className={this.props.classes.badgeIcon} />}
                color="secondary"
            >{result}</Badge>;
        }
        else {
            return result;
        }
    }
}

ObjectTable.propTypes = {
    items: PropTypes.array.isRequired,
    readOnly: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    onEdit: PropTypes.func,
    onAdd: PropTypes.func,
    onReplace: PropTypes.func,
    onDelete: PropTypes.func,
    onUndelete: PropTypes.func,
    onDuplicate: PropTypes.func,
    onSearchTag: PropTypes.func,
    asField: PropTypes.bool,
    pagination: PropTypes.bool
};


const styles = theme => ({
    root: {
        overflowX: "auto"
    },
    rootAsField: {
        boxShadow: "none",
        border: "1px solid #949494",
        overflowX: "auto"
    },
    tableHead: {
        display: "table-header-group"
    },
    tableBody: {
        display: "table-header-group"
    },
    headRow: {
        color: "inherit",
        height: 56,
        display: "table-row",
        outline: "none",
        verticalAlign: "middle"
    },
    tableRow: {
        color: "inherit",
        height: 48,
        display: "table-row",
        outline: "none",
        verticalAlign: "middle"
    },
    tableCell: {
        color: "rgba(0, 0, 0, 0.87)",
        fontSize: "0.8125rem",
        fontWeight: 400
    },
    thumbnail: {
        width: 50,
        height: 50,
        borderRadius: 25,
        objectFit: "cover"
    },
    tagsColumn: {
        fontStyle: "italic"
    },
    headCell: {
        display: "table-cell",
        padding: "4px 56px 4px 24px",
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
        verticalAlign: "inherit",
        color: "rgba(0, 0, 0, 0.54)",
        fontSize: "0.75rem",
        fontWeight: 500,
        paddingRight: 4
    },
    headCellAsField: {
        display: "table-cell",
        padding: "4px 4px 4px 24px",
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
        verticalAlign: "inherit",
        fontSize: "0.75rem",
        fontWeight: 300,
        color: "#bbb",
        height: 30
    },
    cellValue: {
        maxHeight: 48,
        maxWidth: 250,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis"
    },
    addButton: {
        margin: theme.spacing.unit,
    },
    rowActionButton: {
    },
    rowActionCell: {
        textAlign: "right",
        color: "#555555",
        whiteSpace: "nowrap"
    },
    rowTagsCell: {
        whiteSpace: "nowrap"
    },
    iconImage: {
        opacity: 0.75
    },
    grayBadge: {
        backgroundColor: '#aaa'
    },
    redBadge: {
        backgroundColor: '#f77'
    },
    badgeIcon: {
        fontSize: 12,
        color: "#ffffff"
    }
});

const mapStateToProps = (state, props) => {

    // find common fields (excluding image, which is handled specially)
    const uniqueTypeIds = _.uniq(props.items.map(item => item.type));
    const originalFieldsMap = selectTableFieldsMap(state.types, uniqueTypeIds);
    const hasImages = _.flatten(_.values(originalFieldsMap)).includes("#imageurl");
    const fieldsMap = hasImages ? _.mapValues(originalFieldsMap, fields => fields.filter(field => field !== "#imageurl")) : originalFieldsMap;
    const fieldsArray = _.values(fieldsMap);
    const commonFields = _.intersection(...fieldsArray);

    // find names for types
    let typeInfoMap = {}
    for (const id of uniqueTypeIds) {
        const typeDefinition = state.types.typeDefinitions[id];
        typeInfoMap[id] = {
            iconurl: typeDefinition ? typeDefinition.fields.iconurl : undefined,
            name: typeDefinition ? typeDefinition.fields.name || id : id
        };
    }

    // find names for common fields
    let fieldNameMap = {}
    for (const id of commonFields) {
        fieldNameMap[id] = selectFieldName(state.types, uniqueTypeIds[0], id);
    }

    // find uncommon fields for each type
    const commonFieldsSet = new Set(commonFields);
    const uncommonFieldsMap = _.mapValues(fieldsMap, columns => columns.filter(id => !commonFieldsSet.has(id)));
    const hasUncommonFields = _.flatten(_.values(uncommonFieldsMap)).length > 0;

    /*
    console.log("table unique type IDs", uniqueTypeIds);
    console.log("table fields map", fieldsMap);
    console.log("table common fields", commonFields);
    console.log("table type info map", typeInfoMap);
    console.log("table field name map", fieldNameMap);
    console.log("table uncommon fields map", uncommonFieldsMap);
    console.log("table has images", hasImages);
    console.log("table has uncommon fields", hasUncommonFields);
    */

    return {
        uniqueTypeIds: uniqueTypeIds,
        fieldsMap: fieldsMap,
        commonFields: commonFields,
        uncommonFieldsMap: uncommonFieldsMap,
        hasImages: hasImages,
        hasUncommonFields: hasUncommonFields,
        typeInfoMap: typeInfoMap,
        fieldNameMap: fieldNameMap,
        types: state.types,
        authorization: state.authorization,
        objectMap: state.editing.objects,
        translate: (value) => selectTranslation(state.configuration, value)
    };
};

export default connect(mapStateToProps)(withStyles(styles)(ObjectTable));